import { zeroAddress } from 'viem'
import { llamaLendMarketByVault } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { ResupplyMarketsRaw } from './types'

/**
 * The external lending market a Resupply pair wraps.
 *
 * Every Resupply pair's collateral IS another lender's supply position, so a
 * position here carries that market's risk on top of Resupply's own. This
 * resolves the link where we can: the collateral vault is matched against the
 * LlamaLend roster by ADDRESS (LlamaLend indexes markets by Controller, so the
 * lookup goes through `llamaLendMarketByVault`).
 *
 * Verified 2026-08-04: 16 of the 21 registered pairs match a LlamaLend market
 * exactly, and the generation agrees independently — Resupply's own
 * `CurveLend:` names map to LlamaLend `version: 1` and `CurveLendV2:` to
 * `version: 2`. The other 5 are Fraxlend pairs, which we do not integrate as a
 * lender, so they resolve to `provider: 'fraxlend'` with no market key.
 *
 * The roster is an ENRICHMENT, not the source of the identity: the wrapped
 * collateral is read from the vault itself (`collateral_token()` on a Curve
 * Lend vault, `collateralContract()` on a Fraxlend pair — the same probe
 * Resupply's own `Utilities` uses to tell the families apart), so all 21 pairs
 * carry one whether or not LlamaLend metadata is published.
 */
export interface ResupplyWrappedMarket {
  /** Which protocol the collateral position lives in. */
  provider: 'llamalend' | 'fraxlend' | 'unknown'
  /** The ERC-4626 the pair custodies — always known (it IS the collateral). */
  vault: string
  /** `LLAMALEND_<CONTROLLER_ADDR>` when we integrate that market, else absent.
   *  This is the key to look the wrapped market up in our own data. */
  lender?: string
  /** LlamaLend Controller (the borrow surface of the wrapped market). */
  controller?: string
  /** LlamaLend LLAMMA. Carried because a leverage route through Resupply must
   *  never touch it — the wrapped Controller asserts its band state. */
  amm?: string
  /** 1 = `oneway`, 2 = `oneway-v2`. Matches Resupply's CurveLend/CurveLendV2. */
  version?: 1 | 2
  /** What the wrapped market lends against, e.g. `sfrxUSD`. */
  collateralSymbol?: string
  /**
   * The wrapped market's collateral TOKEN.
   *
   * This is the per-market image source. Every CurveLend pair looks identical
   * on our two rows — both are crvUSD/reUSD — so the only thing that visually
   * distinguishes `crvUSD/sfrxUSD` from `crvUSD/WBTC` is the asset the WRAPPED
   * market lends against, which is not one of our rows. Consumers resolve the
   * token icon from this address; the brand icon (`lenderIcon`) stays the
   * fallback and is deliberately still one image for all 21 pairs.
   */
  collateralToken?: string
  collateralDecimals?: number
}

/**
 * Human label for a pair, from its on-chain `name()`.
 *
 * The pair deployer emits `Resupply Pair (CurveLend: crvUSD/sfrxUSD) - 1`:
 * the useful part is inside the parentheses — it names the WRAPPED market,
 * which is the only thing distinguishing one Resupply pair from another. The
 * `- N` suffix is a redeploy counter (there are two `crvUSD/sDOLA` pairs), so
 * it is kept only when it is not `- 1`.
 *
 * Falls back to the raw name rather than inventing one: a pair whose name
 * stops matching this shape should read oddly, not silently lose its identity.
 */
export function resupplyMarketLabel(rawName: string): string {
  const inner = rawName.match(/\(([^)]+)\)/)?.[1]
  if (!inner) return rawName
  const suffix = rawName.match(/\)\s*-\s*(\d+)\s*$/)?.[1]
  return suffix && suffix !== '1' ? `${inner} - ${suffix}` : inner
}

/**
 * The wrapped market's collateral SYMBOL, taken from the label's right-hand
 * side (`CurveLend: crvUSD/sfrxUSD` → `sfrxUSD`).
 *
 * Deliberately not a `symbol()` read: the label already names it, the pair
 * deployer sets it from the wrapped market itself, and an extra multicall round
 * for a display string is one more thing that can come back `'0x'`.
 */
function wrappedCollateralSymbol(rawName: string): string | undefined {
  const inner = rawName.match(/\(([^)]+)\)/)?.[1]
  const sym = inner?.split('/').pop()?.trim()
  return sym && sym.length > 0 ? sym : undefined
}

/** Per-market lender key for a LlamaLend controller (mirrors its own scheme). */
const llamaLendKey = (controller: string) =>
  `LLAMALEND_${controller.replace(/^0x/i, '').toUpperCase()}`

/**
 * Resolve the wrapped market for one Resupply pair. Falls back to
 * `provider: 'fraxlend'` on a Fraxlend pair (its name carries the family) and
 * `'unknown'` when neither matches — a new pair family would land there rather
 * than being silently mislabeled.
 */
function resolveWrappedMarket(
  chainId: string,
  identity: {
    collateral: string
    name: string
    wrappedCollateralToken?: string
    wrappedFamily?: 'curvelend' | 'fraxlend'
  },
): ResupplyWrappedMarket {
  const collateralVault = identity.collateral
  // Read off the vault itself — present for every pair, both families.
  const onChain = {
    collateralToken: identity.wrappedCollateralToken,
    collateralSymbol: wrappedCollateralSymbol(identity.name),
  }

  const market = llamaLendMarketByVault('LLAMALEND', chainId, collateralVault)
  if (market) {
    return {
      provider: 'llamalend',
      vault: collateralVault,
      lender: llamaLendKey(market.controller),
      controller: market.controller,
      amm: market.amm,
      version: market.version,
      // Roster first (curated symbols/decimals), on-chain as the backstop, so
      // an unpublished or lagging roster degrades a label rather than the
      // image address.
      collateralSymbol: market.collateralSymbol ?? onChain.collateralSymbol,
      collateralToken: market.collateralToken ?? onChain.collateralToken,
      collateralDecimals: market.collateralDecimals,
    }
  }

  // No roster entry: the family comes from which getter answered, and the
  // pair's deploy-time name (`Fraxlend: …` / `CurveLend: …`) is only the
  // fallback — a name is never used to resolve an address.
  const family =
    identity.wrappedFamily ??
    (/fraxlend/i.test(identity.name) ? 'fraxlend' : undefined)
  return {
    provider:
      family === 'fraxlend'
        ? 'fraxlend'
        : family === 'curvelend'
          ? 'llamalend'
          : 'unknown',
    vault: collateralVault,
    ...onChain,
  }
}

/**
 * Synthesized per-pair lender key, e.g. `RESUPPLY_1_C5184CCC…`. The chain id
 * rides in the key (Fluid/River/Frankencoin convention) even though Resupply
 * is Ethereum-only today.
 */
export function resupplyLenderKey(
  lender: string,
  chainId: string | number,
  pair: string,
): string {
  return `${lender}_${chainId}_${pair.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, chainId, pair }` from a per-pair key. */
export function resupplyKeyParts(
  key: string,
): { lender: string; chainId: string; pair: string } | undefined {
  if (!key.startsWith('RESUPPLY_')) return undefined
  const suffix = key.slice('RESUPPLY_'.length)
  const m = suffix.match(/^(\d+)_([0-9A-Fa-f]{40})$/)
  if (!m) return undefined
  return {
    lender: 'RESUPPLY',
    chainId: m[1],
    pair: `0x${m[2].toLowerCase()}`,
  }
}

const SECONDS_PER_YEAR = 31_536_000
/** `maxLTV`, `liquidationFee` and `mintFee` are all 1e5-scaled. */
const LTV_PRECISION = 100_000
const ONE = 10n ** 18n

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/** 1e18-scaled per-second rate → nominal APR in percent (package convention). */
function ratePerSecToApr(rate: bigint | null): number {
  if (rate === null) return 0
  return (Number(rate) / 1e18) * SECONDS_PER_YEAR * 100
}

/**
 * Build the reward entries for one pair.
 *
 * Both legs are computed FROM FIRST PRINCIPLES rather than from Resupply's
 * `pairCollateralRewardRates` / `getPairRsupRate` helpers. Those return rates
 * "per debt share" carrying an extra 1e18 (collateral) or 1e36 (RSUP) of
 * padding that the caller is expected to divide back out — a convention that
 * is easy to get silently wrong by orders of magnitude. The raw components are
 * unambiguous, so we use them:
 *
 *  - **Collateral (CRV/CVX via Convex).** `rate` is reward wei/s per 1e18 of
 *    staked shares, so the pair earns `rate x totalCollateralShares / 1e18`
 *    per second. Verified against the Convex pool's own
 *    `crvRewards.rewardRate()/totalSupply()`.
 *  - **Borrow (RSUP).** The pair's slice of the stream is
 *    `rewardRate x weight / totalWeight`. This one is a REBATE: it offsets
 *    what the borrower pays, which is why it lands on `variableBorrowRate`
 *    (the Compound V3 convention) and not on a deposit rate.
 *
 * A reward whose token has no price is DROPPED rather than counted as zero —
 * publishing a 0 % reward reads as "this pays nothing", which is a different
 * claim from "we could not price it".
 */
function buildRewardEntries(
  p: ResupplyMarketsRaw['pairs'][number],
  rsup: ResupplyMarketsRaw['rsup'],
  chainId: string,
  prices: { [asset: string]: number },
  tokens: GenericTokenList,
  collateralUsd: number,
  debtUsd: number,
): { collateral: any[]; borrow: any[] } {
  const priceOf = (address: string): number => {
    const lower = address.toLowerCase()
    const token = tokens[lower]
    const key =
      toOracleKey(token?.assetGroup!) || toGenericPriceKey(lower, chainId)
    return prices[key] ?? 0
  }

  const collateral: any[] = []
  if (collateralUsd > 0) {
    for (const r of p.collateralRewards ?? []) {
      const price = priceOf(r.token)
      if (price <= 0) continue
      const decimals = tokens[r.token.toLowerCase()]?.decimals ?? 18
      // rate x shares / 1e18 = reward base units per second for the pair.
      const perSecond =
        (Number(r.ratePerSecPerShare) * Number(p.totalCollateral ?? 0n)) / 1e18
      const usdPerYear = (perSecond / 10 ** decimals) * SECONDS_PER_YEAR * price
      const apr = (usdPerYear / collateralUsd) * 100
      if (!Number.isFinite(apr) || apr <= 0) continue
      collateral.push({
        asset: r.token,
        depositRate: apr,
        variableBorrowRate: 0,
        stableBorrowRate: 0,
      })
    }
  }

  const borrow: any[] = []
  if (rsup && debtUsd > 0 && (p.rsupWeight ?? 0n) > 0n) {
    const price = priceOf(rsup.govToken)
    if (price > 0) {
      const decimals = tokens[rsup.govToken.toLowerCase()]?.decimals ?? 18
      const perSecond =
        (Number(rsup.rewardRate) * Number(p.rsupWeight)) /
        Number(rsup.totalWeight)
      const usdPerYear = (perSecond / 10 ** decimals) * SECONDS_PER_YEAR * price
      const apr = (usdPerYear / debtUsd) * 100
      if (Number.isFinite(apr) && apr > 0) {
        borrow.push({
          asset: rsup.govToken,
          depositRate: 0,
          // A REBATE on the borrow cost, not a charge.
          variableBorrowRate: apr,
          stableBorrowRate: 0,
        })
      }
    }
  }

  return { collateral, borrow }
}

/**
 * Map one Resupply deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed `RESUPPLY_<chainId>_<PAIR>`.
 *
 * Four modelling decisions worth knowing (all from RESUPPLY.md):
 *
 *  - **The collateral we publish is the UNDERLYING (crvUSD / frxUSD), not the
 *    ERC-4626 share.** The share is an internal accounting unit that no token
 *    list carries and no price feed covers, and both user-facing entry points
 *    (`addCollateral` / `removeCollateral`) are denominated in the underlying.
 *    Share amounts are converted with the vault's own
 *    `convertToAssets(1e18)` — the exact number Resupply's oracle uses. NB
 *    that price is ~**1e15** for Curve Lend vaults, so the share count is
 *    ~1000x the underlying; a bare 1e18 divide is wrong by three orders of
 *    magnitude.
 *  - **The collateral carries the wrapped market's yield.** A Resupply deposit
 *    is a Curve Lend / Fraxlend supply position, so `getUnderlyingSupplyRate`
 *    is published as the collateral row's `intrinsicYield`. Without it the
 *    position looks like it pays nothing, when in fact the whole product is
 *    the spread between that and the ~half-of-it borrow rate.
 *  - **`borrowLimit == 0` means PAUSED.** `pause()` zeroes it and there is no
 *    `isPaused`; 9 of 21 pairs sat at zero at integration. Such a pair is
 *    reported frozen and non-borrowable, but deposits/withdrawals stay open so
 *    users can exit.
 *  - **There is no supply side** — reUSD is minted — so `totalDeposits` on the
 *    loan row is 0 and `depositRate` is 0. The earn leg is sreUSD, which
 *    belongs to the savings provider.
 */
export function convertResupplyMarketsToResponse(
  raw: ResupplyMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  if (!cfg) return out

  const debtAddr = cfg.reusd.toLowerCase()
  const debtDecimals = cfg.debtDecimals ?? 18
  const debtToken = tokens[debtAddr]
  const debtSymbol = debtToken?.symbol ?? 'reUSD'
  const debtPriceKey =
    toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)
  // reUSD is a redemption-floored stablecoin that trades BELOW peg (0.9924 at
  // integration). Fall back to 1 only when we have no feed at all — the error
  // from assuming parity is small, and reporting 0 would zero out every debt
  // figure on the biggest row in the book.
  const debtPrice = prices[debtPriceKey] || 1

  for (const p of raw.pairs ?? []) {
    const id = p.identity

    // A failed read is NOT a zeroed market. Publishing a pair whose `maxLTV`
    // or `borrowLimit` read failed would render it as 0%-LTV and frozen —
    // indistinguishable from a genuinely retired pair, and it would silently
    // pull a live market out of the borrow UI. Skip it instead; the next
    // refresh republishes it. (Observed for real: a 250-call batch can lose
    // individual reads to RPC rate limiting.)
    if (p.maxLTV === null || p.borrowLimit === null) continue
    if (p.totalBorrowAmount === null || p.totalCollateral === null) continue
    // Without the vault share price we cannot convert collateral into the
    // underlying at all, and a 1e18 fallback would be ~1000x wrong on every
    // Curve Lend pair.
    if (p.collateralPrice === null || p.collateralPrice === 0n) continue

    const lenderKey = resupplyLenderKey(raw.lender, chainId, id.pair)

    const collAddr = id.underlying.toLowerCase()
    const collDecimals = id.underlyingDecimals
    const collToken = tokens[collAddr]
    const collSymbol = collToken?.symbol ?? 'COLL'
    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    // Same reasoning: crvUSD/frxUSD are dollar stables and the protocol itself
    // assumes exactly $1 for them (`BasicVaultOracle`).
    const collPrice = prices[collPriceKey] || 1

    // Collateral SHARES → UNDERLYING. `collateralPrice` is
    // `convertToAssets(1e18)`, i.e. underlying raw units per 1e18 shares.
    const shares = p.totalCollateral ?? 0n
    const sharePrice = p.collateralPrice ?? 0n
    const collUnderlyingRaw = sharePrice > 0n ? (shares * sharePrice) / ONE : 0n
    const totalColl = Number(collUnderlyingRaw) / 10 ** collDecimals

    const totalDebt =
      p.totalBorrowAmount !== null
        ? Number(p.totalBorrowAmount) / 10 ** debtDecimals
        : 0
    const borrowLimit =
      p.borrowLimit !== null ? Number(p.borrowLimit) / 10 ** debtDecimals : 0
    const borrowLiquidity = Math.max(borrowLimit - totalDebt, 0)

    // `borrowLimit == 0` is how a pair is paused/retired.
    const halted = (p.borrowLimit ?? 0n) === 0n

    // Which external lending market this pair sits on top of. The wrapped
    // collateral (the per-market image source) is read from the vault itself,
    // so all 21 pairs carry one; the LlamaLend roster additionally supplies
    // the market key/controller for the 16 pairs it covers.
    const wrappedMarket = resolveWrappedMarket(chainId, id)
    // Display label: names the WRAPPED market, which is the only thing that
    // distinguishes one Resupply pair from another (every CurveLend pair's own
    // two rows are crvUSD/reUSD). Without it the config renders as the
    // downstream default, "Disabled".
    const marketLabel = resupplyMarketLabel(id.name)

    // Reward streams. The collateral earns CRV/CVX through Convex; the
    // BORROWER earns RSUP, which rebates part of the interest — at
    // integration ~0.97% against a 2.27% borrow rate, i.e. 43% of the cost.
    // Publishing the rows without these understates the whole product.
    const rewardEntries = buildRewardEntries(
      p,
      raw.rsup,
      chainId,
      prices,
      tokens,
      totalColl * collPrice,
      totalDebt * debtPrice,
    )

    const maxLtv = p.maxLTV !== null ? Number(p.maxLTV) / LTV_PRECISION : 0
    const liquidationPenalty =
      p.liquidationFee !== null ? Number(p.liquidationFee) / LTV_PRECISION : 0
    const mintFeePct =
      p.mintFee !== null ? (Number(p.mintFee) / LTV_PRECISION) * 100 : 0

    // Prefer the lens (recomputed live, including the off-peg amplifier) over
    // `currentRateInfo`, which is only a checkpoint from the last write.
    const borrowApr = ratePerSecToApr(p.liveRatePerSec ?? p.ratePerSec)
    const collateralIntrinsicApr = ratePerSecToApr(p.underlyingSupplyRatePerSec)

    // @ts-ignore — built incrementally, matches the Frankencoin/River converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: id.pair.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: totalColl,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: totalColl,
      borrowLiquidity: 0,
      totalLiquidityUSD: totalColl * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: totalColl * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      // The wrapped Curve Lend / Fraxlend supply rate — this collateral is a
      // lending position and earns while it sits here.
      intrinsicYield: collateralIntrinsicApr,
      // CRV/CVX from the Convex position the pair stakes the collateral into.
      // Empty (not undefined) when the pair does not stake — `convexPid == 0`.
      rewards: rewardEntries.collateral,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          label: marketLabel,
          borrowCollateralFactor: maxLtv,
          collateralFactor: maxLtv,
          borrowFactor: 1,
          liquidationPenalty,
          // Liquidation takes the WHOLE position (`liquidate(borrower)` repays
          // all of it) — there is no partial close.
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      // Deposits stay open on a paused pair so positions can be topped up to
      // avoid liquidation; only borrowing is stopped.
      depositsEnabled: true,
      hasStable: false,
      isActive: !halted,
      isFrozen: halted,
    } as any

    const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: debtSymbol,
      poolId: id.pair.toLowerCase(),
      underlying: debtAddr,
      asset: currencyFor(debtAddr, debtDecimals, debtSymbol, tokens),
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * debtPrice,
      borrowLiquidityUSD: borrowLiquidity * debtPrice,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * debtPrice,
      // Against the governance-set debt ceiling, not a deposit base — there is
      // no supply side to be utilized.
      utilization: borrowLimit > 0 ? Math.min(totalDebt / borrowLimit, 1) : 0,
      depositRate: 0,
      variableBorrowRate: borrowApr,
      stableBorrowRate: 0,
      // Pinned to ~half the greater of {wrapped supply rate, sfrxUSD risk-free
      // rate, floor}, then scaled by the reUSD off-peg weight. No utilization
      // curve anywhere in the protocol.
      rateModel: 'protocolSet',
      // `mintFee` is added to the debt at borrow time. Zero on every live pair,
      // but it is governance-settable.
      originationFee: mintFeePct,
      intrinsicYield: 0,
      // RSUP emissions to BORROWERS — a rebate on `variableBorrowRate`, not a
      // deposit yield. Empty when the emission stream has expired.
      rewards: rewardEntries.borrow,
      decimals: debtDecimals,
      config: {
        0: {
          category: 0,
          label: marketLabel,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: halted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !halted,
      depositsEnabled: false,
      hasStable: false,
      variableBorrowDisabled: halted,
      isActive: !halted,
      isFrozen: halted,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketLabel,
        loanDecimals: debtDecimals,
        collateralDecimals: collDecimals,
        id: id.pair.toLowerCase(),
        lltv: p.maxLTV !== null ? p.maxLTV.toString() : '0',
        // The oracle is the collateral vault's own `convertToAssets`; there is
        // no external price feed contract to name.
        oracle: zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        // --- Resupply descriptor (consumed by the calldata builders, the
        // worker-api resolvers and the loop quoter) ---
        resupply: {
          pair: id.pair,
          /** The pair's raw on-chain `name()`, before the label is derived. */
          rawName: id.name,
          label: marketLabel,
          /** The ERC-4626 share the pair actually books as collateral. */
          collateralVault: id.collateral,
          collateralVaultDecimals: id.collateralDecimals,
          /** What the user deposits/withdraws (crvUSD or frxUSD). */
          underlying: id.underlying,
          /**
           * The EXTERNAL lending market this pair wraps. A Resupply position
           * inherits that market's risk (bad debt there is bad debt here), so
           * the link is first-class rather than a footnote. Resolved against
           * our own LlamaLend roster by vault address where possible.
           */
          wrappedMarket,
          /** `convertToAssets(1e18)` — UNDERLYING per 1e18 shares. ~1e15 for
           *  Curve Lend vaults. Required to size any share-denominated arg. */
          collateralPrice: (p.collateralPrice ?? 0n).toString(),
          /** The pair's cached `1e36 / collateralPrice` (stale between writes). */
          exchangeRate: (p.exchangeRate ?? 0n).toString(),
          maxLTV: (p.maxLTV ?? 0n).toString(),
          borrowLimit: (p.borrowLimit ?? 0n).toString(),
          /** Hard floor per position — a smaller borrow, or a repay leaving
           *  less than this, reverts. */
          minimumBorrowAmount: (p.minimumBorrowAmount ?? 0n).toString(),
          liquidationFee: (p.liquidationFee ?? 0n).toString(),
          mintFee: (p.mintFee ?? 0n).toString(),
          totalBorrowShares: (p.totalBorrowShares ?? 0n).toString(),
          totalBorrowAmount: (p.totalBorrowAmount ?? 0n).toString(),
          totalCollateralShares: (p.totalCollateral ?? 0n).toString(),
          borrowRatePerSecond: (
            p.liveRatePerSec ??
            p.ratePerSec ??
            0n
          ).toString(),
          underlyingSupplyRatePerSecond: (
            p.underlyingSupplyRatePerSec ?? 0n
          ).toString(),
          /** Zero borrow limit = paused by governance (no `isPaused` exists). */
          paused: halted,
          addresses: {
            registry: cfg.registry,
            reusd: cfg.reusd,
            utilities: cfg.utilities ?? undefined,
            curveSwapper: cfg.curveSwapper ?? undefined,
            ensoSwapper: cfg.ensoSwapper ?? undefined,
            lifiSwapper: cfg.lifiSwapper ?? undefined,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
